import { InboxArrowDownIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setBorrowerGroupData, setLoanDetail } from 'actions'
import cloneDeep from 'clone-deep'
import { LockSectionControl } from 'components/FieldLock'
import {
  type InputType,
  appApiUrl,
  customOptionLabels,
  editableFieldOptions,
  editableOptionFields,
  FieldOrigin,
  InputSelect,
  IVisibleProp,
} from 'config'
import { useLockContext } from 'context/LockContext'
import { usePermissions } from 'hooks/usePermissions'
import type { ILoan, ILoanDetail } from 'pages/LoanStructure/interfaces'
import { isNonDSCRProduct } from 'pages/LoanStructure/Logic'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteAdditionalPropertyInfo, getGeoNameList, updateAdditionalPropertyInfo } from 'services'
import Api from 'services/api'
import { combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { FormTable, FormTableHeader, Tooltip } from 'stories/components'
import {
  canEditLoanApplication,
  formatDate,
  getItemsFromFullAddress,
  getPrice3decimal,
  isEmpty,
  openAuditLog,
  removeComma,
  renderTableHistoryValue,
} from 'utils'
import { isLoanFieldPropHidden } from 'utils/loan'

import { BorrowerSeperator } from '../config'
import { propertyInfoValidate } from '../PropertyInformation/logic'
import {
  additionalFieldsForAdmin,
  adminHeader,
  defaultInputs,
  mapTaxFieldKeys,
  nonAdminHeader,
  taxFieldKeys,
} from './constant'
import type { IAdditionalPropertyInformation } from './interface'
import { isAdditionalPropertyValidated, isPassedCrossCollateralLimit } from './logics'

interface IProps {
  setLoading: (value: boolean) => void
}

export const AdditionalPropertyInformation = (props: IProps) => {
  const loanNumber = Api.getLoanNumber()
  const { hasPermission } = usePermissions()

  const dispatch = useDispatch()
  const { borrower, auth, loan, loanDetail, fieldProperties, masterOptionProps, sectionTitle } = useSelector(
    (state: any) => {
      const loanFields = state.globalConfigReducer[GlobalConfigType.LoanFields] || {}
      return {
        loan: state.loan as ILoan,
        borrower: state.borrower.borrower,
        auth: state.auth,
        loanDetail: state.loanDetail as ILoanDetail,
        fieldProperties: (loanFields[FieldOrigin.AdditionalPropertyAddresses] || {}) as Record<string, IVisibleProp>,
        // Loan Structure owns the option lists a tenant renames; every section reads them from there.
        masterOptionProps: (loanFields[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>,
        sectionTitle: (loanFields['customTitle'] || {})[FieldOrigin.AdditionalPropertyAddresses] as string | undefined,
      }
    },
  )
  const isAdmin = hasPermission('Loan.Application.AdditionalPropertyInformation.Full')
  const { resolveCanEdit } = useLockContext()
  const additionalPropertyScope = {
    type: 'subsection' as const,
    borrowerSeperator: BorrowerSeperator.BORROWER,
    fieldOrigin: FieldOrigin.PropertyInformation,
    fieldKey: 'additionalPropertyAddresses',
  }
  const canEdit = resolveCanEdit(additionalPropertyScope, canEditLoanApplication())

  const [data, setData] = useState<IAdditionalPropertyInformation[]>([])
  const [tableInputs, setTableInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [loanData, setLoanData] = useState<ILoan>(loan)

  useEffect(() => {
    const newInputs = cloneDeep(tableInputs)

    if (!isAdmin) additionalFieldsForAdmin.forEach((key) => delete newInputs[key])
    setTableInputs(newInputs)
  }, [isAdmin])

  const isNonDSCR = useMemo(() => {
    return isNonDSCRProduct(loan.productType)
  }, [loan.productType])

  useEffect(() => {
    if (isNonDSCR) {
      setTableInputs((prev) => {
        const newInputs = cloneDeep(prev)
        newInputs.monthlyRent.disabled = false
        newInputs.annualTaxAmount.disabled = false
        newInputs.annualHOAFees.disabled = false
        newInputs.insuranceAmount.disabled = false
        newInputs.floodInsurance.disabled = false
        newInputs.schoolTax.disabled = false
        newInputs.otherTax.disabled = false
        return newInputs
      })
    }
  }, [isNonDSCR])

  useEffect(() => {
    const hasSectionProps = !!Object.keys(fieldProperties).length
    const hasRenamedOptions = editableOptionFields.some((key) => editableFieldOptions(key, {}, masterOptionProps))
    if (!hasSectionProps && !hasRenamedOptions) return

    setTableInputs((prev) => {
      const newInputs = cloneDeep(prev)
      Object.keys(fieldProperties).forEach((key) => {
        if (!newInputs[key]) return
        const prop = fieldProperties[key]
        if (prop.title) newInputs[key].title = prop.title
        if (prop.type) newInputs[key].type = prop.type as any
        if (prop.required !== undefined) newInputs[key].required = prop.required
        newInputs[key].visible = isLoanFieldPropHidden(prop, loan) ? false : true
        if (prop.defaultValue !== undefined && prop.defaultValue !== '') newInputs[key].value = prop.defaultValue
      })

      // Separate from the loop above: a renamed list is read from Loan Structure, so the field
      // need not appear in this section's own fieldProperties at all.
      Object.keys(newInputs).forEach((key) => {
        const renamed = customOptionLabels(editableFieldOptions(key, fieldProperties, masterOptionProps))
        if (renamed) (newInputs[key] as any).options = renamed
      })
      return newInputs
    })
  }, [fieldProperties, masterOptionProps, loan])

  useEffect(() => {
    setData(borrower.additionalPropertyInfo)
  }, [borrower])

  useEffect(() => {
    setLoanData(loan)
  }, [loan])

  const renderStatus = (item: IAdditionalPropertyInformation, index: number) => {
    const isValid = isAdditionalPropertyValidated(item)

    if (isValid) {
      if (item.overrideValidation)
        return (
          <div>
            <div className="text-green-800 bg-green-100 rounded py-1 px-2 w-fit">
              <p>Eligible</p>
              <p className="border-t">Override</p>
            </div>
          </div>
        )

      const { rateData } = loanDetail
      const { crossCollateralLimit, allocatedLoanAmount } = borrower
      if (!isEmpty(crossCollateralLimit) && !!allocatedLoanAmount.length) {
        const targetProperty = loanData.multipleProperties.find(
          (v) => v.subjectPropertyAddress === item.propertyAddress,
        )
        let dscr = 0
        if (!!targetProperty) {
          const pitis = combinedPropertyMonthlyExpense(loanData, targetProperty, loanDetail.rateData)
          dscr = removeComma(pitis.dscr)
        }
        const { isPassed, errors } = isPassedCrossCollateralLimit(
          item,
          dscr,
          crossCollateralLimit,
          allocatedLoanAmount[index],
          rateData.type,
        )
        if (isPassed) return <div className="text-green-800 bg-green-100 rounded py-1 px-2 w-fit">Eligible</div>
        else
          return (
            <div className="flex cursor-pointer">
              <Tooltip
                message={`<div class="w-[175px] whitespace-normal leading-[14px]">${errors.join('<br/>')}</div>`}
              >
                <div className="py-1 px-2 bg-red-200 text-red-900 rounded">
                  <div className="flex items-center gap-1 rounded">
                    Ineligible
                    <QuestionMarkCircleIcon className="w-3 h-3"></QuestionMarkCircleIcon>
                  </div>
                </div>
              </Tooltip>
            </div>
          )
      } else {
        return (
          <div className="flex cursor-pointer">
            <Tooltip message="Interest Rate & Base Price is Required!">
              <div className="py-1 px-2 bg-red-200 text-red-900 rounded">
                <div className="flex items-center gap-1 rounded">
                  Ineligible
                  <QuestionMarkCircleIcon className="w-3 h-3"></QuestionMarkCircleIcon>
                </div>
              </div>
            </Tooltip>
          </div>
        )
      }
    } else return <div className="text-red-900 bg-red-200 rounded py-1 px-2 w-fit">Incomplete</div>
  }

  const convertData = (sourceData: IAdditionalPropertyInformation[]) => {
    let rlt: any = []

    if (isAdmin)
      sourceData.map((item, index) => {
        const targetProperty = loanData.multipleProperties.find(
          (v) => v.subjectPropertyAddress === item.propertyAddress,
        )
        if (!targetProperty) return
        const pitis = combinedPropertyMonthlyExpense(loanData, targetProperty, loanDetail.rateData)
        let json: any = {
          ...item,
          propertyTypeAddress: `<span><div class="border-b w-full mb-2">${
            item.propertyType || 'N/A'
          }</div><div class="border-b w-full mb-2">${item.propertyAddress}</div><div class="border-b w-full mb-2">${
            item.propertyAPN || 'N/A'
          }</div>${item.numberOfUnits || 'N/A'}</span>`,
          asIsCdaValue: `<span><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.purchasePrice,
          )}</div><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.asIsValue,
          )}</div><div class="border-b w-full mb-2">${
            item.cdaValue ? '$ ' + getPrice3decimal(item.cdaValue) : 'N/A'
          }</div>${item.acquisitionDate ? formatDate(item.acquisitionDate) : 'N/A'}</span>`,
          occupancyRentContract: `<span><div class="border-b w-full mb-2">${
            item.occupancy || 'N/A'
          }</div><div class="border-b w-full mb-2">$ ${getPrice3decimal(item.monthlyRent)}</div>${
            item.monthlyRentFromAppraisal ? '$ ' + getPrice3decimal(item.monthlyRentFromAppraisal) : 'N/A'
          }</span>`,
          annualTaxHOAHazardFlood: `<span><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.annualTaxAmount,
          )}</div><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.annualHOAFees,
          )}</div><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.insuranceAmount,
          )}</div>$ ${getPrice3decimal(item.floodInsurance)}</span>`,
          dscrValues: `<span><div class="border-b w-full mb-2">${getPrice3decimal(pitis.dscr)} %</div>${
            removeComma(pitis.sum) !== 0
              ? getPrice3decimal((removeComma(item.monthlyRentFromAppraisal) / removeComma(pitis.sum)).toFixed(2)) +
                ' %'
              : 'N/A'
          }</span>`,
          status: renderStatus(item, index),
          dscrBasedOnAppraisal:
            removeComma(pitis.sum) !== 0
              ? (removeComma(item.monthlyRentFromAppraisal) / removeComma(pitis.sum)).toFixed(2)
              : 0,
          dscr: removeComma(pitis.dscr),
        }
        rlt.push(json)
      })
    else
      sourceData.map((item, index) => {
        const targetProperty = loanData.multipleProperties.find(
          (v) => v.subjectPropertyAddress === item.propertyAddress,
        )
        if (!targetProperty) return
        const pitis = combinedPropertyMonthlyExpense(loanData, targetProperty, loanDetail.rateData)
        let json: any = {
          ...item,
          propertyTypeAddress: `<span><div class="border-b w-full mb-2">${
            item.propertyType || 'N/A'
          }</div><div class="border-b w-full mb-2">${item.propertyAddress}</div>${item.numberOfUnits || 'N/A'}</span>`,
          asIsValueMonthlyRent: `<span><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.purchasePrice,
          )}</div><div class="border-b w-full mb-2">$ ${getPrice3decimal(item.asIsValue)}</div>$ ${getPrice3decimal(
            item.monthlyRent,
          )}</span>`,
          annualTaxHOA: `<span><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.annualTaxAmount,
          )}</div>$ ${getPrice3decimal(item.annualHOAFees)}</span>`,
          hazardFlood: `<span><div class="border-b w-full mb-2">$ ${getPrice3decimal(
            item.insuranceAmount,
          )}</div>$ ${getPrice3decimal(item.floodInsurance)}</span>`,
          dscrValues: `<span><div class="border-b w-full mb-2">${item.occupancy || 'N/A'}</div>${getPrice3decimal(
            pitis.dscr,
          )} %</span>`,
          status: renderStatus(item, index),
          dscrBasedOnAppraisal:
            removeComma(pitis.sum) !== 0
              ? (removeComma(item.monthlyRentFromAppraisal) / removeComma(pitis.sum)).toFixed(2)
              : 0,
          dscr: removeComma(pitis.dscr),
        }
        rlt.push(json)
      })

    return rlt
  }

  const getData = useMemo(() => {
    return convertData(data)
  }, [data, loanData])

  const onRemove = async (id: any) => {
    return new Promise((resolve) => {
      props.setLoading(true)

      deleteAdditionalPropertyInfo(id)
        .then(() => {
          resolve(true)

          let newData: IAdditionalPropertyInformation[] = []

          borrower.additionalPropertyInfo.map((item: any) => {
            if (item.id !== id) newData.push(item)
          })
          dispatch(setBorrowerGroupData(BorrowerSeperator.BORROWER, { additionalPropertyInfo: newData }))
          setData(newData)
        })
        .finally(() => props.setLoading(false))
    })
  }

  const onSubmit = (currentId: any, values: Record<string, any>) => {
    return new Promise((resolve) => {
      if (!currentId) {
        resolve(values)
        return
      }
      let newData = cloneDeep(borrower.additionalPropertyInfo)

      props.setLoading(true)

      updateAdditionalPropertyInfo(currentId, values)
        .then((allocatedLoanAmount) => {
          dispatch(
            setLoanDetail({
              allocatedLoanAmount,
            }),
          )
          resolve(values)
          newData.map((item: any, index: number) => {
            if (item.id === currentId) newData[index] = { ...item, ...values }
          })
          dispatch(setBorrowerGroupData(BorrowerSeperator.BORROWER, { additionalPropertyInfo: newData }))
          setData(newData)
        })
        .finally(() => {
          props.setLoading(false)
          propertyInfoValidate(true, true)
        })
    })
  }

  const showHistoryFormTable = async (key: string, headers: Array<FormTableHeader> = []) => {
    const options = {
      table: 'AdditionalPropertyInformation',
      field: `${key}`,
      keys: {
        field: key,
      },
      renderValue: headers.length ? (tableData: any) => renderTableHistoryValue(convertData(tableData), headers) : null,
    }
    openAuditLog(options)
  }

  const inputLogic = (
    data: Record<string, InputType>,
    currentId?: number,
    currentKey?: string,
  ): Record<string, InputType> | Promise<Record<string, InputType>> => {
    if (!!currentKey) return data

    return (async () => {
      const propertyAddressObj: any = await getItemsFromFullAddress(data.propertyAddress.value)
      const list = propertyAddressObj.postal_code ? (await getGeoNameList(propertyAddressObj.postal_code)) ?? [] : []
      if (!list?.length) {
        data.propertyCounty.inputType = 'text'
        return data
      }
      const options = list
        .map((v: any) => v.adminName2)
        .filter((v: string) => !!v)
        .map((v: string) => (v.endsWith('County') ? v : `${v} County`))
      data.propertyCounty.inputType = 'select'
      ;(data.propertyCounty as InputSelect).hasDefaultOption = true
      data.propertyCounty.options = options
      return data
    })()
  }

  const onChangeTableInputs = (newData: Record<string, InputType>, key: string) => {
    if (key !== 'monthlyRentFromAppraisal' && !taxFieldKeys.includes(key)) return

    const newInputs = cloneDeep(newData)
    const newLoanData = cloneDeep(loanData)
    const propertyAddress = String(newInputs.propertyAddress?.value ?? '').trim()
    if (!propertyAddress) return

    const propertyIndex = newLoanData.multipleProperties.findIndex(
      (v) => String(v.subjectPropertyAddress ?? '').trim() === propertyAddress,
    )
    if (propertyIndex === -1) return

    if (taxFieldKeys.includes(key)) {
      const loanFieldKey = mapTaxFieldKeys[key]
      const value =
        key === 'monthlyRent'
          ? removeComma(newInputs.monthlyRent.value)
          : Math.round(removeComma(newInputs[key].value) / 12)
      ;(newLoanData.multipleProperties[propertyIndex] as any)[loanFieldKey] = value
    }

    const targetProperty = newLoanData.multipleProperties[propertyIndex]
    const pitis = combinedPropertyMonthlyExpense(newLoanData, targetProperty, loanDetail.rateData)
    const monthlySum = removeComma(pitis.sum)
    const monthlyRentFromAppraisal = removeComma(newInputs.monthlyRentFromAppraisal.value)
    const dscrBaseAppraisal = monthlySum === 0 ? 0 : monthlyRentFromAppraisal / monthlySum
    newInputs.dscrBasedOnAppraisal.value = dscrBaseAppraisal.toFixed(2)
    newInputs.dscr.value = removeComma(pitis.dscr).toFixed(2)

    setLoanData(newLoanData)
    setTableInputs(newInputs)
  }

  if (loan.isMultipleProperties) {
    return (
      <div>
        <div className="border-b my-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-[17px]">- {sectionTitle || 'Additional Property Addresses'}</p>
            <LockSectionControl
              scope={additionalPropertyScope}
              label={sectionTitle || 'Additional Property Addresses'}
              size="md"
            />
          </div>

          {isAdmin && (
            <Tooltip message="Download Excel">
              <a
                href={`${appApiUrl}/loan/additionalPropertyInfo/totalDownload/${loanNumber}?token=${auth.token}`}
                download
                className="p-1 hover-shadow1 cursor-pointer transition-all duration-200 rounded ml-3 mr-2"
              >
                <InboxArrowDownIcon className="w-5 h-5 text-shade-blue" />
              </a>
            </Tooltip>
          )}
        </div>

        <FormTable
          header={isAdmin ? adminHeader : nonAdminHeader}
          permission={2.5}
          inputs={tableInputs}
          addText=""
          data={getData}
          showModal
          onSubmit={onSubmit}
          onRemove={onRemove}
          inputLogic={inputLogic}
          onChangeInputs={onChangeTableInputs}
          showHistory={(headers) => showHistoryFormTable('additionalPropertyInfo', headers)}
          sectionLocked={!canEdit}
        />
      </div>
    )
  } else return null
}
